
import { useState } from "react";
import { Building2, MoreVertical, Check, Trash, Edit, Lock, Unlock, Star } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import type { Database } from "@/integrations/supabase/types";
import { format } from "date-fns";
import EditOrganizationDialog from "./EditOrganizationDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Organization = Database['public']['Tables']['organizations']['Row'];

interface OrganizationTileProps {
  organization: Organization;
  onGoToOrganization: () => void;
  onMarkDefault?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleLock?: () => void;
  isDefault?: boolean;
  isTrial?: boolean;
}

const OrganizationTile = ({ 
  organization, 
  onGoToOrganization,
  onMarkDefault,
  onEdit,
  onDelete,
  onToggleLock,
  isDefault,
  isTrial = true
}: OrganizationTileProps) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Generate a consistent color based on organization name
  const getOrganizationColor = (name: string) => {
    const colors = [
      'bg-[#9b87f5]', // Primary Purple
      'bg-[#7E69AB]', // Secondary Purple
      'bg-[#0EA5E9]', // Ocean Blue
      'bg-[#1EAEDB]', // Bright Blue
      'bg-[#6E59A5]', // Tertiary Purple
    ];
    
    // Use string hash to pick a consistent color
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Direct implementation of Mark as default
  const handleMarkDefault = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication error",
          description: "You need to be logged in to perform this action",
          variant: "destructive",
        });
        return;
      }

      console.log("Setting default organization:", organization.id, "for user:", user.id);
      
      // First, update all memberships to set is_default to false
      const { error: resetError } = await supabase
        .from("organization_members")
        .update({ is_default: false })
        .eq("user_id", user.id);
        
      if (resetError) {
        console.error("Error resetting previous defaults:", resetError);
        throw resetError;
      }
      
      // Then set the selected organization as default
      const { error: updateError } = await supabase
        .from("organization_members")
        .update({ is_default: true })
        .eq("organization_id", organization.id)
        .eq("user_id", user.id);
      
      if (updateError) {
        console.error("Error setting default organization:", updateError);
        throw updateError;
      }
      
      toast({
        title: "Default organization set",
        description: `${organization.name} is now your default organization.`,
        variant: "success",
      });
      
      // Call the onMarkDefault callback if provided
      if (onMarkDefault) {
        onMarkDefault();
      }
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["default-organization"] });
      queryClient.invalidateQueries({ queryKey: ["default-organization-redirect"] });
      queryClient.invalidateQueries({ queryKey: ["default-organization-nav"] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to set default organization",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Card className="relative">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 sm:p-6">
          <div className="flex items-start gap-3 w-full sm:w-auto">
            {organization.logo_url ? (
              <img 
                src={organization.logo_url} 
                alt={organization.name} 
                className="w-11 h-11 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${getOrganizationColor(organization.name)}`}>
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-lg sm:text-xl truncate">{organization.name}</CardTitle>
                <div className="flex flex-wrap gap-1">
                  {isTrial && (
                    <Badge variant="secondary" className="text-xs">
                      TRIAL
                    </Badge>
                  )}
                  {organization.is_locked && (
                    <Badge variant="destructive" className="text-xs">
                      LOCKED
                    </Badge>
                  )}
                  {isDefault && (
                    <Badge variant="outline" className="text-xs bg-primary/10 border-primary/20">
                      <Star className="h-3 w-3 mr-1 text-primary" />
                      DEFAULT
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">
                Created on {format(new Date(organization.created_at || ''), 'yyyy/MM/dd')}
              </div>
              <div className="text-xs sm:text-sm">
                <span className="text-muted-foreground">Edition:</span> {organization.country || 'N/A'}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              variant="secondary"
              className="whitespace-nowrap flex-1 sm:flex-none text-sm h-9"
              onClick={onGoToOrganization}
            >
              Go to organization
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="flex-shrink-0 h-9 w-9">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!isDefault && (
                  <DropdownMenuItem 
                    onClick={handleMarkDefault}
                    className="cursor-pointer"
                  >
                    <Star className="mr-2 h-4 w-4" />
                    Mark as default
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)} className="cursor-pointer">
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                {onToggleLock && (
                  <DropdownMenuItem onClick={onToggleLock} className="cursor-pointer">
                    {organization.is_locked ? (
                      <>
                        <Unlock className="mr-2 h-4 w-4" />
                        Unlock Organization
                      </>
                    ) : (
                      <>
                        <Lock className="mr-2 h-4 w-4" />
                        Lock Organization
                      </>
                    )}
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem 
                    onClick={onDelete}
                    className="text-destructive cursor-pointer"
                  >
                    <Trash className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
      </Card>

      <EditOrganizationDialog
        organization={organization}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={onEdit}
      />
    </>
  );
};

export default OrganizationTile;
